import { useState, useCallback, useRef, useEffect } from 'react'
import { TestSuite, getAuthHeaders } from '../api'

interface StreamingState {
  isStreaming: boolean
  streamedText: string
  parsedResult: TestSuite | null
  jobId: string | null
  error: string | null
  errorCode: string | null
  retryAfterSeconds: number | null
  usage: {
    inputTokens: number
    outputTokens: number
  } | null
}

interface UseStreamingGenerationOptions {
  streamingUrl: string
  onTextChunk?: (text: string) => void
  onComplete?: (result: TestSuite, jobId: string) => void
  onError?: (error: string) => void
}

const INACTIVITY_TIMEOUT_MS = 60000 // 60 seconds

export function useStreamingGeneration(options: UseStreamingGenerationOptions) {
  const { streamingUrl, onTextChunk, onComplete, onError } = options

  const [state, setState] = useState<StreamingState>({
    isStreaming: false,
    streamedText: '',
    parsedResult: null,
    jobId: null,
    error: null,
    errorCode: null,
    retryAfterSeconds: null,
    usage: null,
  })

  const abortControllerRef = useRef<AbortController | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const resetInactivityTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      setState((prev) => ({
        ...prev,
        error: 'Generation timed out. The server may be experiencing high load. Please try again.',
        isStreaming: false,
      }))
      onError?.('Generation timed out. The server may be experiencing high load. Please try again.')
    }, INACTIVITY_TIMEOUT_MS)
  }, [onError])

  const generate = useCallback(
    async (payload: {
      requirement?: string
      images?: Array<{ base64: string; mediaType: string }>
      additionalContext?: string
      customInstructions?: string
      jiraTicket?: string
      jiraIncludeFields?: Record<string, boolean>
      figmaUrl?: string
      testCaseCount?: number
      autoCount?: boolean
      detailLevel?: 'concise' | 'standard' | 'detailed'
      targetDevices?: string[]
      targetBrowsers?: string[]
    }) => {
      // Abort any existing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      abortControllerRef.current = new AbortController()

      setState({
        isStreaming: true,
        streamedText: '',
        parsedResult: null,
        jobId: null,
        error: null,
        errorCode: null,
        retryAfterSeconds: null,
        usage: null,
      })

      try {
        // Connection timeout - abort if no response within 30s
        const connectionTimeout = setTimeout(() => {
          abortControllerRef.current?.abort()
        }, 30000)

        const response = await fetch(streamingUrl, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
          signal: abortControllerRef.current.signal,
        })

        clearTimeout(connectionTimeout)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        if (!response.body) {
          throw new Error('Response body is null')
        }

        // Start inactivity timer after connection is established
        resetInactivityTimer()

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let accumulatedText = ''
        let currentJobId: string | null = null
        // Track current event across chunks (large data lines may span multiple chunks)
        let currentEvent = ''
        let currentData = ''

        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            break
          }

          // Reset inactivity timer on each chunk received
          resetInactivityTimer()

          buffer += decoder.decode(value, { stream: true })

          // Parse SSE events from buffer
          const lines = buffer.split('\n')
          buffer = lines.pop() || '' // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7)
            } else if (line.startsWith('data: ')) {
              currentData = line.slice(6)

              if (currentEvent && currentData) {
                try {
                  const data = JSON.parse(currentData)

                  switch (currentEvent) {
                    case 'connected':
                      currentJobId = data.jobId
                      setState((prev) => ({
                        ...prev,
                        jobId: data.jobId,
                      }))
                      break

                    case 'text_delta':
                      accumulatedText += data.text
                      setState((prev) => ({
                        ...prev,
                        streamedText: accumulatedText,
                      }))
                      onTextChunk?.(data.text)
                      break

                    case 'message_stop':
                      if (data.parsed_result) {
                        setState((prev) => ({
                          ...prev,
                          parsedResult: data.parsed_result,
                          usage: data.usage,
                          jobId: data.jobId || currentJobId,
                        }))
                        onComplete?.(data.parsed_result, data.jobId || currentJobId || '')
                      } else {
                        // Backend failed to parse the response (truncation, invalid JSON, etc.)
                        // Treat as an error so the frontend clears loading state
                        const errorMsg = data.parsed_result === null
                          ? 'Generation completed but results could not be parsed. Try reducing test count or detail level.'
                          : 'Unknown generation error'
                        setState((prev) => ({
                          ...prev,
                          error: errorMsg,
                          isStreaming: false,
                        }))
                        onError?.(errorMsg)
                      }
                      break

                    case 'error':
                      setState((prev) => ({
                        ...prev,
                        error: data.message,
                        errorCode: typeof data.code === 'string' ? data.code : null,
                        retryAfterSeconds:
                          typeof data.retryAfterSeconds === 'number' ? data.retryAfterSeconds : null,
                        isStreaming: false,
                      }))
                      onError?.(data.message)
                      break

                    case 'done':
                      setState((prev) => ({
                        ...prev,
                        isStreaming: false,
                      }))
                      break
                  }
                } catch (parseError) {
                  console.error('Failed to parse SSE data:', parseError)
                }
              }
            } else if (line === '') {
              // Reset for next event
              currentEvent = ''
              currentData = ''
            }
          }
        }

        // Stream completed - clear inactivity timer and ensure streaming state is cleared.
        // This is a safety net: if the 'done' SSE event was lost (e.g., Lambda timeout
        // killed the stream mid-write), isStreaming would stay true forever.
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
        setState((prev) => ({
          ...prev,
          isStreaming: false,
        }))
      } catch (error) {
        // Clear inactivity timer on any error
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }

        if (error instanceof Error && error.name === 'AbortError') {
          console.log('Stream aborted')
          setState((prev) => ({
            ...prev,
            isStreaming: false,
          }))
          return
        }

        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        setState((prev) => ({
          ...prev,
          error: errorMessage,
          isStreaming: false,
        }))
        onError?.(errorMessage)
      }
    },
    [streamingUrl, onTextChunk, onComplete, onError, resetInactivityTimer]
  )

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      setState((prev) => ({
        ...prev,
        isStreaming: false,
      }))
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const reset = useCallback(() => {
    setState({
      isStreaming: false,
      streamedText: '',
      parsedResult: null,
      jobId: null,
      error: null,
      errorCode: null,
      retryAfterSeconds: null,
      usage: null,
    })
  }, [])

  return {
    ...state,
    generate,
    abort,
    reset,
  }
}
